import { t as tr } from "../i18n";
// Time-range filtering for the admin dashboard summary numbers.
//
// This is PURE date math and DISPLAY-ONLY. Given a range key and "now", it
// returns the inclusive lower-bound timestamp to compare a record's date
// against — or null for "All Time" (no lower bound). Callers filter an
// already-loaded array in the browser; nothing here reads, writes, or recomputes
// any stored money. It only decides which existing records fall inside the
// chosen window. Pairs with the multi-currency display helper in ./currency —
// time filtering narrows *which* records are summed; currency splitting keeps
// each currency separate when they're summed. The two are independent.

export type TimeRangeKey = "1w" | "4w" | "1y" | "mtd" | "ytd" | "all";

export interface TimeRangeOption {
  key: TimeRangeKey;
  label: string; // compact pill label
  title: string; // tooltip / full description
}

// Display order of the range pills, left → right.
export const TIME_RANGE_OPTIONS: TimeRangeOption[] = [
  { key: "1w", label: "1 Week", title: tr("dateRange.rollingTheLastDays") },
  { key: "4w", label: "4 Weeks", title: tr("dateRange.rollingTheLastDays2") },
  { key: "1y", label: "1 Year", title: tr("dateRange.rollingTheLastDays3") },
  { key: "mtd", label: "MTD", title: tr("dateRange.monthToDateSince") },
  { key: "ytd", label: "YTD", title: tr("dateRange.yearToDateSince") },
  { key: "all", label: tr("dateRange.allTime"), title: tr("dateRange.everyRecordNoDate") },
];

const DAY_MS = 24 * 60 * 60 * 1000;

// Human-readable description of the active window, shown next to the totals so
// it's always clear what the numbers cover.
export function rangeTitle(key: TimeRangeKey): string {
  return TIME_RANGE_OPTIONS.find((o) => o.key === key)?.title ?? tr("dateRange.allTime");
}

/**
 * Inclusive lower-bound (epoch ms) for a range, or null for "All Time".
 * Rolling windows (1w/4w/1y) count back from `now`; MTD/YTD anchor to the start
 * of the current calendar month/year in the viewer's LOCAL time — which is what
 * "this month" / "this year" means to the admin reading the screen. `now` is
 * injectable so the logic can be unit-tested with a fixed clock.
 */
export function rangeStart(key: TimeRangeKey, now: Date = new Date()): number | null {
  const t = now.getTime();
  switch (key) {
    case "1w":
      return t - 7 * DAY_MS;
    case "4w":
      return t - 28 * DAY_MS;
    case "1y":
      return t - 365 * DAY_MS;
    case "mtd":
      return new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    case "ytd":
      return new Date(now.getFullYear(), 0, 1).getTime();
    case "all":
    default:
      return null;
  }
}

/**
 * Parse a record's date field to epoch ms, or null if it's missing or
 * unparseable. Used both to filter AND to *count* undated records, so the UI can
 * surface them instead of letting them silently vanish from a bounded range and
 * quietly skew the totals.
 */
export function parseRecordTime(value: unknown): number | null {
  if (!value) return null;
  const t = new Date(value as string).getTime();
  return Number.isFinite(t) ? t : null;
}

export interface RangePartition<T> {
  inside: T[]; // records that fall within the window (and count toward totals)
  undated: T[]; // records with no usable date
}

/**
 * Split records into those inside the window and those with no usable date.
 *
 * - Bounded range (1w/4w/1y/mtd/ytd): a record counts only if its date is
 *   present AND >= the lower bound. Undated records are excluded from `inside`
 *   and reported in `undated` so the caller can warn the admin.
 * - All Time: there's no lower bound, so every record — including undated ones —
 *   is kept in `inside` (nothing is dropped, so nothing is skewed). Undated
 *   records are still listed in `undated` for an informational count, but the
 *   caller should NOT show an "excluded" warning for All Time.
 */
export function partitionByRange<T>(
  records: T[],
  key: TimeRangeKey,
  getDate: (r: T) => unknown,
  now: Date = new Date()
): RangePartition<T> {
  const start = rangeStart(key, now);
  const inside: T[] = [];
  const undated: T[] = [];
  for (const r of records) {
    const t = parseRecordTime(getDate(r));
    if (t === null) {
      undated.push(r);
      if (start === null) inside.push(r); // All Time keeps undated in the totals
      continue;
    }
    if (start === null || t >= start) inside.push(r);
  }
  return { inside, undated };
}
